import React from 'react';

import {GwgAccelerator} from '@udemy/career-academies';
import {useI18n} from '@udemy/i18n';
import {GwgCheckoutProvider, GrowWithGoogleBannerWrapper} from '@udemy/subscription-browse';
import {useActiveSubscriptions} from '@udemy/subscription-graphql';

import {GwgFooter} from 'src/components/grow-with-google/gwg-footer/gwg-footer.react-component';
import {GrowWithGoogleTestimonials} from 'src/components/grow-with-google/testimonials/testimonials.react-component';
import {AiLearningPathsSection} from 'src/features/google-ai-plan-page/components/ai-learning-paths';
import {CareerStatsCitations} from 'src/features/google-ai-plan-page/components/career-stats-citations/career-stats-citations.react-component';
import {CareerStats} from 'src/features/google-ai-plan-page/components/career-stats/career-stats.react-component';
import {ExplorePlanSection} from 'src/features/google-ai-plan-page/components/explore-plan-section';
import {FeatureCards} from 'src/features/google-ai-plan-page/components/feature-cards';
import {FreeResourcesSection} from 'src/features/google-ai-plan-page/components/free-resources-section';
import {HeroBanner} from 'src/features/google-ai-plan-page/components/hero-banner';
import {ReimagineCareerCarousel} from 'src/features/google-ai-plan-page/components/reimagine-career-carousel/reimagine-career-carousel.react-component';
import styles from 'src/features/google-ai-plan-page/google-ai-plan-page.module.less';
import {isSubscriberToAnyGwgPlan} from 'src/features/google-ai-plan-page/utils/is-gwg-subscriber';
import {computePlanStats} from 'src/features/google-ai-plan-page/utils/plan-stats';
import {GetGrowWithGoogleEnrollmentsQuery} from 'src/generated/graphql';
import {useGwgPlanPreference} from 'src/hooks/useGwgPlanPreference';

import {GwgPlanData} from './hooks/useGetGwgPlanData';
import {ApiInstructor} from './types/course';

export interface GoogleAiPlanPageProps {
    plan?: GwgPlanData;
    title?: string;
    enrollmentsData?: GetGrowWithGoogleEnrollmentsQuery;
    instructor?: ApiInstructor;
    allGwgAccelerators?: GwgAccelerator[];
}

export const GoogleAiPlanPage = ({
    title,
    enrollmentsData,
    plan,
    instructor,
    allGwgAccelerators,
}: GoogleAiPlanPageProps) => {
    const {gettext} = useI18n();
    const activeSubscription = useActiveSubscriptions()[0];
    const isGwgSubscriber = isSubscriberToAnyGwgPlan(activeSubscription);
    const planStats = React.useMemo(
        () => computePlanStats(allGwgAccelerators),
        [allGwgAccelerators],
    );
    const planPreference = useGwgPlanPreference();

    return (
        <GwgCheckoutProvider planPreference={planPreference}>
            <div>
                <HeroBanner title={title} enrollmentsData={enrollmentsData} />

                {!isGwgSubscriber && (
                    <div className={'ud-container'}>
                        <CareerStats />
                    </div>
                )}

                <div className={styles.container}>
                    {!isGwgSubscriber && <ExplorePlanSection planStats={planStats} />}

                    {allGwgAccelerators && allGwgAccelerators.length > 0 && (
                        <div className={'ud-container'}>
                            <AiLearningPathsSection allGwgAccelerators={allGwgAccelerators} />
                        </div>
                    )}

                    <ReimagineCareerCarousel />

                    {!isGwgSubscriber && (
                        <div className={'ud-container'}>
                            <FeatureCards />
                        </div>
                    )}

                    {!isGwgSubscriber && <GrowWithGoogleBannerWrapper planStats={planStats} />}
                </div>

                <FreeResourcesSection />

                {!isGwgSubscriber && (
                    <div className={styles.container}>
                        <div className={'ud-container'}>
                            <div className={styles['testimonials-container']}>
                                <GrowWithGoogleTestimonials
                                    title={gettext('Empowering more people with AI')}
                                    testimonials={plan?.testimonials || []}
                                />
                            </div>

                            <div className={styles['stats-support']}>
                                <CareerStatsCitations />
                            </div>
                        </div>
                    </div>
                )}

                <div className={'ud-container'}>
                    <GwgFooter
                        instructor={instructor}
                        frequentlyAskedQuestions={plan?.frequentlyAskedQuestions || []}
                    />
                </div>
            </div>
        </GwgCheckoutProvider>
    );
};
